import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';

import { useCheckPagePayment } from './useCheckPagePayment';

import styles from './DuplicateCardConfirmationModal.module.scss';

const sectionId = 'DuplicateCardPaymentModal';

type DuplicateCardConfirmationModalControl = ReturnType<typeof useCheckPagePayment>['modal']['duplicateCard'];
type DuplicateCardConfirmationModalProps = {
  control: DuplicateCardConfirmationModalControl;
};


const DuplicateCardConfirmationModal: FC<DuplicateCardConfirmationModalProps> = ({ control, ...rest }) => {
  const { t } = useTranslation();
  const { details } = control;
  return (
    <Modal
      data-testid={sectionId}
      open={control.isOpen}
      onBackDropClick={() => control.close('on-user-cancel')}
      {...rest}
    >
      <ModalHeader closeButton={<CloseIconButton onClick={() => control.close('on-user-cancel')} />}>
        <ModalTitle copy={t('payment.info.duplicateCardTitle')} />
      </ModalHeader>
      <ModalContent flex={{ justifyContent: 'flex-start' }}>
        {details && (
          <Flex alignItems="center" className={styles.content}>
            <Copy variant="h4" value={t('labels.util.currency', { value: details.nextAmount })} />
            <Flex horizontal gap="sm" alignItems="center" justifyContent="center">
              <FontIcon size="sm" name="credit_card" />
              <Copy variant="body" value={t('payment.info.duplicateCardTransaction', { cardBrand: details.cardBrand, lastFourCcNumber: details.lastFour })} />
            </Flex>
            <Copy variant="body"
              className={styles.message}
              value={t('payment.info.duplicateCard', { amount: details.prevAmount })} />
          </Flex>
        )}
      </ModalContent>
      <ModalActions>
        <ModalAction label="cancel" variant="outlined" onClick={() => control.close('on-user-cancel')} />
        <ModalAction label="confirm" color="primary" onClick={control.onConfirm} />
      </ModalActions>
    </Modal>
  );
};

export { DuplicateCardConfirmationModal };
